import { logEvent } from 'components/telemetry';
import { DBConfig } from 'config';
import 'firebase/firestore';
import { getContentStorageUrl } from 'helpers';
import { ThunkDispatch } from 'redux-thunk';
import { getDataById } from 'store/data/generic/selectors';
import { updateOperation } from 'store/data/ops/actions';
import { ApplicationState, ContentType, Operation } from 'types';
import {
	BatchRecordWrite,
	BatchWriteOptions,
	BatchWriteResult,
	chunkWrites,
	commitWithRetry,
	DEFAULT_BATCH_CONCURRENCY,
	isRetryableWriteError,
	MAX_BATCH_SIZE,
} from 'helpers/bulkEdit/batching';
import firebase, { fireDB } from './initconfig';

type FBDocChange = firebase.firestore.DocumentChange<firebase.firestore.DocumentData>;
type FBTimestamp = firebase.firestore.Timestamp;
const fbFromDate = firebase.firestore.Timestamp.fromDate;

export interface FireStoreModule {
	getModuleName: () => string;
	getOpsKeySuffix: () => string;
	getFBWatchQuery: (db: firebase.firestore.Firestore) => firebase.firestore.Query<firebase.firestore.DocumentData>;
	getChangeProcessAction: (changes: FBDocChange[]) => any;
}

class SnapChange implements FBDocChange {
	constructor(private record) {
		for (let key in record) {
			if (record[key] && typeof record[key] === 'object' && typeof record[key]._nanoseconds !== 'undefined') {
				record[key] = new firebase.firestore.Timestamp(record[key]._seconds, record[key]._nanoseconds);
			}
		}
	}

	public type: firebase.firestore.DocumentChangeType = 'added';
	public oldIndex = 0;
	public newIndex = 0;
	public doc = {
		data: () => {
			return this.record;
		},
		exists: true,
		ref: null as unknown as firebase.firestore.DocumentReference,
		id: this.record.id,
		metadata: '' as unknown as firebase.firestore.SnapshotMetadata,
		isEqual: () => false,
		get: () => {},
	};
}

export class InitFireStoreModules {
	private static readonly BufferTime: number = 1;
	private getState: () => ApplicationState;
	private dispatch: ThunkDispatch<ApplicationState, any, any>;
	private unsubscribe: { [moduleName: string]: () => void } = {};
	// private allmodules: { [moduleName: string]: FireStoreModule | null } = {};

	constructor(getState: () => ApplicationState, dispatch: ThunkDispatch<ApplicationState, any, any>) {
		this.getState = getState;
		this.dispatch = dispatch;
	}

	private getUniqueKey = (module: FireStoreModule) => module.getModuleName() + '+' + module.getOpsKeySuffix();

	private getOpsKey(module: FireStoreModule) {
		return 'last-fb-fetch-timestamp::' + this.getUniqueKey(module);
	}

	private async processChanges(module: FireStoreModule, fetchTimeKey: string, changes: FBDocChange[]) {
		await this.dispatch(module.getChangeProcessAction(changes));

		let now = new Date();
		let fbFetchOp = this.getState().opsState[fetchTimeKey] || {
			key: fetchTimeKey,
			createdAt: now,
			value: null,
			updatedAt: now,
		};
		let maxUpdatedTime: FBTimestamp = fbFetchOp.value;
		if (!maxUpdatedTime) {
			maxUpdatedTime = fbFromDate(new Date(0));
		}

		changes.forEach((change) => {
			if (change.type === 'added' || change.type === 'modified') {
				let updatedAt: FBTimestamp = change.doc.data().updatedAt;
				if (updatedAt.seconds > maxUpdatedTime.seconds) {
					maxUpdatedTime = updatedAt;
				} else if (updatedAt.seconds === maxUpdatedTime.seconds && updatedAt.nanoseconds > maxUpdatedTime.nanoseconds) {
					maxUpdatedTime = updatedAt;
				}

				// 	if (module.getModuleName() === 'configs' && change.doc.id.endsWith(DBConfig.UpdatedAt(''))) {
				// 		let collection = change.doc.id.replace(DBConfig.UpdatedAt(''), '');
				// 		let module = this.allmodules[collection];
				// 		if (!module) {
				// 			this.allmodules[collection] = null;
				// 		} else {
				// 			if (!this.unsubscribe[this.getUniqueKey(module)]) {
				// 				this.initModule(module, false, undefined, undefined, true);
				// 			}
				// 		}
				// 	}
			}
			if (change.type === 'removed') {
				// This case won't be there as we are not removing anything
				// In case this comes up due to direct manupulation on server
				// we ignore this even and do not change update time
				// next refresh time, we'll not get any changes from cur updated time
				// and we still keep the upgrade time as it is
			}
		});

		fbFetchOp.value = maxUpdatedTime;
		fbFetchOp.updatedAt = now;

		await this.dispatch(updateOperation(fbFetchOp));
	}

	private async initModule(
		module: FireStoreModule,
		initWithSnapshot?: boolean,
		snapshotTime?: number,
		snapShotJson?: any
		// forceSubscribe?: boolean
	) {
		return new Promise(async (resolve, reject) => {
			try {
				let db = fireDB;

				let fetchTimeKey = this.getOpsKey(module);
				let fbFetchOp: Operation = this.getState().opsState[fetchTimeKey] || ({} as Operation);
				let fetchTime: FBTimestamp = fbFetchOp.value;

				let moduleName = module.getModuleName();

				if (initWithSnapshot && snapshotTime && snapShotJson) {
					let snapData = snapShotJson[module.getModuleName()];
					if (snapData) {
						let changes = snapData.map((record) => new SnapChange(record));

						console.log('Processing snapshot for module(' + moduleName + '):');
						await this.processChanges(module, fetchTimeKey, changes);

						fetchTime = fbFromDate(new Date(snapshotTime));
					} else {
						if (!fetchTime) {
							fetchTime = fbFromDate(new Date(0));
						} else {
							fetchTime = new firebase.firestore.Timestamp(fetchTime.seconds, fetchTime.nanoseconds);
						}
					}
				} else {
					if (!fetchTime) {
						fetchTime = fbFromDate(new Date(0));
					} else {
						fetchTime = new firebase.firestore.Timestamp(fetchTime.seconds, fetchTime.nanoseconds);
					}
				}
				//  else {
				// 	fetchTime = new firebase.firestore.Timestamp(fetchTime.seconds, fetchTime.nanoseconds);
				// 	// let bufferedMillis = (fetchTime.seconds - InitFireStoreModules.BufferTime) * 1000;
				// 	// fetchTime = firebase.firestore.Timestamp.fromMillis(bufferedMillis);
				// }

				let query = module.getFBWatchQuery(db);

				let promiseNotHandled = true;

				// if (module.getModuleName() !== 'configs' && forceSubscribe !== true && this.allmodules[module.getModuleName()] !== null) {
				// 	this.allmodules[module.getModuleName()] = module;
				// 	resolve(true);
				// 	return;
				// }

				this.unsubscribe[this.getUniqueKey(module)] = query
					.where('updatedAt', '>', fetchTime)
					.orderBy('updatedAt', 'desc')
					// .limit(50)
					.onSnapshot(
						async (snapshot) => {
							try {
								console.log('Updates for module(' + moduleName + '):');
								if (snapshot.metadata.fromCache === true) {
									console.log('updates from cache');
									// return;
								}
								let updates = snapshot.docChanges();

								if (updates.length === 0) {
									// return;
								}

								await this.processChanges(module, fetchTimeKey, updates);
								promiseNotHandled && resolve(true);
								promiseNotHandled = false;
							} catch (error) {
								console.error('FB: onSnapshot: ' + error.message, error.stack);
								promiseNotHandled && reject(error);
								promiseNotHandled = false;
							}
						},
						(error) => {
							console.error('FB: onSnapshot: ' + error.message, error.stack);
						}
					);
			} catch (error) {
				console.error('FB: initModule: ' + error.message, error.stack);
			}
		});
	}

	public async initModules(modules: FireStoreModule[], initWithSnapshot?: boolean) {
		try {
			let snapShotJson = undefined;
			let timestamp: number | undefined = undefined;
			let fbFetchOp: Operation | undefined = undefined;
			if (initWithSnapshot === true) {
				try {
					let configs = getDataById(this.getState(), 'configs') || {};
					let uri = configs[DBConfig.Snapshot]?.uri;
					timestamp = configs[DBConfig.Snapshot]?.timestamp;
					if (timestamp && uri) {
						let fetchTimeKey = 'snapshot';
						fbFetchOp = this.getState().opsState[fetchTimeKey] || ({} as Operation);
						let fetchTime: number = fbFetchOp.value;
						if (!fetchTime || fetchTime < timestamp) {
							console.log('Fetching snapshot of time: ' + timestamp);
							let snapshotUrl = getContentStorageUrl(ContentType.Data, uri);
							let result = await window.fetch(snapshotUrl);
							snapShotJson = await result.json();

							let now = new Date();
							fbFetchOp = this.getState().opsState[fetchTimeKey] || ({ key: fetchTimeKey, createdAt: now } as Operation);
							fbFetchOp.value = timestamp;
							fbFetchOp.updatedAt = now;

							await this.dispatch({
								type: 'HACK_STATE',
								path: 'dataState.articles',
								value: { byId: {}, filtered: [] },
							});
							await this.dispatch({
								type: 'HACK_STATE',
								path: 'dataState.rhymes',
								value: { byId: {}, filtered: [] },
							});
							await this.dispatch({
								type: 'HACK_STATE',
								path: 'dataState.publications',
								value: { byId: {}, filtered: [] },
							});
							await this.dispatch({
								type: 'HACK_STATE',
								path: 'dataState.editions',
								value: { byId: {}, filtered: [] },
							});
							await this.dispatch({
								type: 'HACK_STATE',
								path: 'dataState.booklets',
								value: { byId: {}, filtered: [] },
							});
							await this.dispatch({
								type: 'HACK_STATE',
								path: 'dataState.notifications',
								value: { byId: {}, filtered: [] },
							});
							await this.dispatch({
								type: 'HACK_STATE',
								path: 'dataState.quotes',
								value: { byId: {}, filtered: [] },
							});
							await this.dispatch({
								type: 'HACK_STATE',
								path: 'dataState.hd',
								value: { byId: {}, filtered: [] },
							});
						}
					}
				} catch (error) {
					console.error('FB: initModules: ' + error.message, error.stack);
				}
			}

			let allInits: any[] = [];
			for (let i = 0; i < modules.length; i++) {
				allInits.push(this.initModule(modules[i], initWithSnapshot, timestamp, snapShotJson));
			}

			await Promise.all(allInits);

			if (snapShotJson && fbFetchOp) {
				await this.dispatch(updateOperation(fbFetchOp));
			}

			// let snap = await firebase.firestore().collection('anumati').where('ams', '==', false).where('publishTime', '<=', firebase.firestore.Timestamp.now()).where('publishTime', '>', firebase.firestore.Timestamp.fromDate(new Date('2023-10-13 00:00:00'))).get();
			// let records = snap.docs;
			// records.forEach((record) => console.log(record.data()));
		} catch (error) {
			console.error('FB: initModules: ' + error.message, error.stack);
		}
	}

	public async unsubscribeModule(module: FireStoreModule, clearFetchTime: boolean = true) {
		if (this.unsubscribe[this.getUniqueKey(module)]) {
			this.unsubscribe[this.getUniqueKey(module)]();
			delete this.unsubscribe[this.getUniqueKey(module)];
		}

		if (clearFetchTime) {
			let fetchTimeKey = this.getOpsKey(module);
			let fbFetchOp = this.getState().opsState[fetchTimeKey] || ({} as Operation);
			fbFetchOp.value = null;
			fbFetchOp.updatedAt = new Date();

			await this.dispatch(updateOperation(fbFetchOp));
		}
	}
}

export const getServerTime = () => {
	return new Date().getTime();
};

export const generateRecordId = (collection) => {
	return fireDB.collection(collection).doc().id;
};

export const setRecord = async (collection, recordId, values, byName = undefined) => {
	let db = fireDB;
	let query: any = db.collection(collection);

	values = {
		...values,
		by: firebase.auth().currentUser?.uid,
		updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
	};

	if (values.id) {
		delete values.id;
	}

	if (byName) {
		values.byName = byName;
	}

	if (recordId) {
		await query.doc(recordId).set(values, { merge: true });
		logEvent('setRecord', { ...values, updatedAt: null, createdAt: null });

		// await db.collection('configs').doc(DBConfig.UpdatedAt(collection)).update({
		// 	value: firebase.firestore.FieldValue.serverTimestamp(),
		// 	by: firebase.auth().currentUser?.uid,
		// 	updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
		// });

		return recordId;
	}
};

export type { BatchRecordWrite, BatchWriteFailure, BatchWriteOptions, BatchWriteResult } from 'helpers/bulkEdit/batching';
export { chunkWrites, commitWithRetry, DEFAULT_BATCH_CONCURRENCY, isRetryableWriteError, MAX_BATCH_SIZE } from 'helpers/bulkEdit/batching';

export const deleteRecord = async (collection, recordId) => {
	let db = fireDB;
	let query: any = db.collection(collection);

	if (recordId) {
		await query.doc(recordId).delete();
		logEvent('deleteRecord', { collection, recordId, by: firebase.auth().currentUser?.uid });

		return recordId;
	}
};

function buildBatchValues(values: { [key: string]: any }) {
	let prepared: { [key: string]: any } = {
		...values,
		by: firebase.auth().currentUser?.uid,
		updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
	};

	// The document id lives in the path, never in the body.
	delete prepared.id;

	return prepared;
}

async function commitWritesIndividually(collection: string, writes: BatchRecordWrite[]): Promise<BatchWriteResult> {
	let result: BatchWriteResult = { succeeded: [], failed: [] };
	let query: any = fireDB.collection(collection);

	for (let write of writes) {
		try {
			if (write.remove) {
				await query.doc(write.id).delete();
			} else {
				await query.doc(write.id).set(buildBatchValues(write.values ?? {}), { merge: true });
			}

			result.succeeded.push(write.id);
		} catch (error: any) {
			result.failed.push({ id: write.id, error: error?.message ?? String(error) });
		}
	}

	return result;
}

/**
 * Commits many record writes as chunked Firestore batches.
 *
 * Writes mirror `setRecord`, so the `updatedAt` watermark the local sync relies on keeps
 * advancing. A batch that fails is retried one record at a time, so a single bad document
 * cannot take the whole chunk down and the caller learns exactly which records failed.
 */
export const writeRecordsBatch = async (
	collection: string,
	writes: BatchRecordWrite[],
	options: BatchWriteOptions = {}
): Promise<BatchWriteResult> => {
	let result: BatchWriteResult = { succeeded: [], failed: [] };

	if (!collection || !writes?.length) {
		return result;
	}

	let chunkSize = Math.min(Math.max(options.chunkSize ?? MAX_BATCH_SIZE, 1), MAX_BATCH_SIZE);
	let concurrency = Math.max(options.concurrency ?? DEFAULT_BATCH_CONCURRENCY, 1);
	let chunks = chunkWrites(writes, chunkSize);
	let completed = 0;
	let cancelled = false;

	async function commitChunk(writesInChunk: BatchRecordWrite[]) {
		let ids = writesInChunk.map((write) => write.id);
		options.onChunkStart?.(ids);

		function buildBatch() {
			let batch = fireDB.batch();

			for (let write of writesInChunk) {
				let docRef = fireDB.collection(collection).doc(write.id);

				if (write.remove) {
					batch.delete(docRef);
				} else {
					batch.set(docRef, buildBatchValues(write.values ?? {}), { merge: true });
				}
			}

			return batch;
		}

		try {
			await commitWithRetry(() => buildBatch().commit(), {
				isCancelled: options.isCancelled,
				onRetry: (attempt) => options.onChunkRetry?.(ids, attempt),
			});
			result.succeeded.push(...ids);
			options.onChunkSettled?.(ids, []);
		} catch (error: any) {
			if (isRetryableWriteError(error)) {
				// Still busy after backing off. Retrying as single writes would only add load,
				// so the chunk is reported as failed and can be run again later.
				let message = error?.message ?? 'Firestore is busy, please try again';
				let failed = ids.map((id) => ({ id, error: message }));
				result.failed.push(...failed);
				options.onChunkSettled?.([], failed);
			} else {
				// A rejected value or permission problem belongs to specific records, so the
				// chunk is replayed one at a time to find out which.
				let individual = await commitWritesIndividually(collection, writesInChunk);
				result.succeeded.push(...individual.succeeded);
				result.failed.push(...individual.failed);
				options.onChunkSettled?.(individual.succeeded, individual.failed);
			}
		}

		completed += writesInChunk.length;
		options.onProgress?.(completed);
	}

	for (let i = 0; i < chunks.length; i += concurrency) {
		if (cancelled || options.isCancelled?.()) {
			cancelled = true;
			break;
		}

		await Promise.all(chunks.slice(i, i + concurrency).map(commitChunk));
	}

	return result;
};

export default firebase;
